import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Globe, 
  Users, 
  DollarSign, 
  Plus,
  Edit,
  Save,
  X
} from 'lucide-react';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface BusinessProfile {
  businessName: string;
  legalName: string;
  businessType: 'goods' | 'services' | 'hybrid';
  registrationNumber: string;
  taxIdentificationNumber: string;
  description: string;
  website: string;
  phone: string;
  email: string;
  physicalAddress: {
    street: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  postalAddress: {
    street: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  businessLifecycle: 'startup' | 'active' | 'dormant' | 'closed';
}

interface Jurisdiction {
  id: string;
  country: string;
  taxAuthority: string;
  taxAuthorityId: string;
  registrationNumber: string;
  taxIdentificationNumber: string;
  isPrimary: boolean;
  complianceModules: string[];
  status: 'active' | 'inactive' | 'suspended';
}

interface Department {
  id: string;
  name: string;
  description: string;
  managerId: string;
  parentDepartmentId: string;
  costCenter: string;
  budget: number;
  employees: string[];
}

interface Currency {
  id: string;
  currencyCode: string;
  isPrimary: boolean;
  exchangeRate: number;
  settings: any;
}

const BusinessProfilePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'jurisdictions' | 'departments' | 'currencies'>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [showJurisdictionForm, setShowJurisdictionForm] = useState(false);
  const [showDepartmentForm, setShowDepartmentForm] = useState(false);
  const [showCurrencyForm, setShowCurrencyForm] = useState(false);

  // Form states
  const [profileForm, setProfileForm] = useState<Partial<BusinessProfile>>({});
  const [jurisdictionForm, setJurisdictionForm] = useState<Partial<Jurisdiction>>({});
  const [departmentForm, setDepartmentForm] = useState<Partial<Department>>({});
  const [currencyForm, setCurrencyForm] = useState<Partial<Currency>>({});

  useEffect(() => {
    loadBusinessProfile();
    loadJurisdictions();
    loadDepartments();
    loadCurrencies();
  }, []);

  const loadBusinessProfile = async () => {
    try {
      const response = await api.get('/api/business-profile/profile');
      setProfile(response.data.data);
      setProfileForm(response.data.data);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load business profile');
    } finally {
      setLoading(false);
    }
  };

  const loadJurisdictions = async () => {
    try {
      const response = await api.get('/api/business-profile/jurisdictions');
      setJurisdictions(response.data.data || []);
    } catch (error: any) {
      toast.error('Failed to load jurisdictions');
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await api.get('/api/business-profile/departments');
      setDepartments(response.data.data || []);
    } catch (error: any) {
      toast.error('Failed to load departments');
    }
  };

  const loadCurrencies = async () => {
    try {
      const response = await api.get('/api/business-profile/currencies');
      setCurrencies(response.data.data || []);
    } catch (error: any) {
      toast.error('Failed to load currencies');
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/api/business-profile/profile', profileForm);
      setProfile(profileForm as BusinessProfile);
      setEditingProfile(false);
      toast.success('Business profile updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const addJurisdiction = async () => {
    setSaving(true);
    try {
      await api.post('/api/business-profile/jurisdictions', jurisdictionForm);
      setShowJurisdictionForm(false);
      setJurisdictionForm({});
      loadJurisdictions();
      toast.success('Jurisdiction added successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to add jurisdiction');
    } finally {
      setSaving(false);
    }
  };

  const addDepartment = async () => {
    setSaving(true);
    try {
      await api.post('/api/business-profile/departments', departmentForm);
      setShowDepartmentForm(false);
      setDepartmentForm({});
      loadDepartments();
      toast.success('Department created successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create department');
    } finally {
      setSaving(false);
    }
  };

  const addCurrency = async () => {
    setSaving(true);
    try {
      await api.post('/api/business-profile/currencies', currencyForm);
      setShowCurrencyForm(false);
      setCurrencyForm({});
      loadCurrencies();
      toast.success('Currency added successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to add currency');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Business Profile</h1>
        <p className="text-gray-600 mt-2">Manage your business identity, structure, and compliance settings</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'profile', name: 'Business Information', icon: Building2 },
            { id: 'jurisdictions', name: 'Jurisdictions', icon: Globe },
            { id: 'departments', name: 'Departments', icon: Users },
            { id: 'currencies', name: 'Currencies', icon: DollarSign },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="mr-2 h-5 w-5" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && profile && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Business Information</h2>
              <div className="flex space-x-2">
                {editingProfile ? (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditingProfile(false);
                        setProfileForm(profile);
                      }}
                      disabled={saving}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={saveProfile}
                      disabled={saving}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => setEditingProfile(true)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.businessName || '') : profile.businessName}
                  onChange={(e) => setProfileForm({ ...profileForm, businessName: e.target.value })}
                  disabled={!editingProfile}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Legal Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.legalName || '') : profile.legalName}
                  onChange={(e) => setProfileForm({ ...profileForm, legalName: e.target.value })}
                  disabled={!editingProfile}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.businessType || '') : profile.businessType}
                  onChange={(e) => setProfileForm({ ...profileForm, businessType: e.target.value as any })}
                  disabled={!editingProfile}
                >
                  <option value="goods">Goods</option>
                  <option value="services">Services</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registration Number
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.registrationNumber || '') : profile.registrationNumber}
                  onChange={(e) => setProfileForm({ ...profileForm, registrationNumber: e.target.value })}
                  disabled={!editingProfile}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Identification Number
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.taxIdentificationNumber || '') : profile.taxIdentificationNumber}
                  onChange={(e) => setProfileForm({ ...profileForm, taxIdentificationNumber: e.target.value })}
                  disabled={!editingProfile}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Lifecycle
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.businessLifecycle || '') : profile.businessLifecycle}
                  onChange={(e) => setProfileForm({ ...profileForm, businessLifecycle: e.target.value as any })}
                  disabled={!editingProfile}
                >
                  <option value="startup">Startup</option>
                  <option value="active">Active</option>
                  <option value="dormant">Dormant</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={editingProfile ? (profileForm.description || '') : profile.description}
                  onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                  disabled={!editingProfile}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.website || '') : profile.website}
                  onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                  disabled={!editingProfile}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.email || '') : profile.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  disabled={!editingProfile}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editingProfile ? (profileForm.phone || '') : profile.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  disabled={!editingProfile}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Jurisdictions Tab */}
      {activeTab === 'jurisdictions' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Tax Jurisdictions</h2>
              <Button
                onClick={() => setShowJurisdictionForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Jurisdiction
              </Button>
            </div>

            {showJurisdictionForm && (
              <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Jurisdiction</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={jurisdictionForm.country || ''}
                      onChange={(e) => setJurisdictionForm({ ...jurisdictionForm, country: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tax Authority</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={jurisdictionForm.taxAuthority || ''}
                      onChange={(e) => setJurisdictionForm({ ...jurisdictionForm, taxAuthority: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Registration Number</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={jurisdictionForm.registrationNumber || ''}
                      onChange={(e) => setJurisdictionForm({ ...jurisdictionForm, registrationNumber: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID Number</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={jurisdictionForm.taxIdentificationNumber || ''}
                      onChange={(e) => setJurisdictionForm({ ...jurisdictionForm, taxIdentificationNumber: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowJurisdictionForm(false);
                      setJurisdictionForm({});
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={addJurisdiction}
                    disabled={saving}
                  >
                    {saving ? 'Adding...' : 'Add Jurisdiction'}
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {jurisdictions.map((jurisdiction) => (
                <div key={jurisdiction.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {jurisdiction.country} - {jurisdiction.taxAuthority}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Registration: {jurisdiction.registrationNumber} | Tax ID: {jurisdiction.taxIdentificationNumber}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {jurisdiction.isPrimary && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Primary
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        jurisdiction.status === 'active' 
                          ? 'bg-green-100 text-green-800'
                          : jurisdiction.status === 'inactive'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {jurisdiction.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Departments Tab */}
      {activeTab === 'departments' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Departments</h2>
              <Button
                onClick={() => setShowDepartmentForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Department
              </Button>
            </div>

            {showDepartmentForm && (
              <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Department</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department Name</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={departmentForm.name || ''}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cost Center</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={departmentForm.costCenter || ''}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, costCenter: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      value={departmentForm.description || ''}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Budget</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={departmentForm.budget || ''}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, budget: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowDepartmentForm(false);
                      setDepartmentForm({});
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={addDepartment}
                    disabled={saving}
                  >
                    {saving ? 'Creating...' : 'Create Department'}
                  </Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {departments.map((department) => (
                <div key={department.id} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900">{department.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{department.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Cost Center: {department.costCenter}</span>
                    {department.budget && (
                      <span className="font-medium text-gray-900">
                        ${(department.budget || 0).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Currencies Tab */}
      {activeTab === 'currencies' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Currencies</h2>
              <Button
                onClick={() => setShowCurrencyForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Currency
              </Button>
            </div>

            {showCurrencyForm && (
              <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Currency</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency Code</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={currencyForm.currencyCode || ''}
                      onChange={(e) => setCurrencyForm({ ...currencyForm, currencyCode: e.target.value.toUpperCase() })}
                      placeholder="USD"
                      maxLength={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Exchange Rate</label>
                    <input
                      type="number"
                      step="0.0001"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={currencyForm.exchangeRate || ''}
                      onChange={(e) => setCurrencyForm({ ...currencyForm, exchangeRate: parseFloat(e.target.value) })}
                      placeholder="1.0000"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={currencyForm.isPrimary || false}
                        onChange={(e) => setCurrencyForm({ ...currencyForm, isPrimary: e.target.checked })}
                      />
                      <span className="text-sm font-medium text-gray-700">Primary Currency</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowCurrencyForm(false);
                      setCurrencyForm({});
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={addCurrency}
                    disabled={saving}
                  >
                    {saving ? 'Adding...' : 'Add Currency'}
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {currencies.map((currency) => (
                <div key={currency.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{currency.currencyCode}</h3>
                      <p className="text-sm text-gray-600">
                        Exchange Rate: {currency.exchangeRate?.toFixed(4)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {currency.isPrimary && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Primary
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessProfilePage;
